import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/* Recipes / BOM and sales consumption (ADR-0026). Quantities: base unit, scale 1000. */

export const recipeComponentSchema = z.object({
  id: uuidSchema,
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  qty_scaled: z.number().int(),
  /** Current weighted-average cost of this quantity; null without cost history. */
  cost_minor: z.number().int().nullable(),
});

export const recipeSchema = z.object({
  id: uuidSchema,
  target_type: z.enum(['menu_item', 'modifier_option']),
  target_id: uuidSchema,
  target_name: z.string(),
  notes: z.string().nullable(),
  active: z.boolean(),
  version: z.number().int(),
  components: z.array(recipeComponentSchema),
  /** Σ component cost per portion; null if any component has no cost yet. */
  cost_minor: z.number().int().nullable(),
  /** Menu price for menu items (food cost % = cost ÷ price). */
  price_minor: z.number().int().nullable(),
  currency: z.string(),
});
export type Recipe = z.infer<typeof recipeSchema>;
export const recipeListResponseSchema = z.object({ items: z.array(recipeSchema) });

const componentInput = z.object({
  item_id: uuidSchema,
  /** Quantity per portion in `unit` (scale 1000). */
  qty_scaled: z.number().int().positive().refine(Number.isSafeInteger),
  unit: z.string().trim().min(1).max(12).optional(),
});
export const createRecipeRequestSchema = z.object({
  target_type: z.enum(['menu_item', 'modifier_option']),
  target_id: uuidSchema,
  notes: z.string().trim().min(1).max(300).nullable().default(null),
  components: z.array(componentInput).min(1).max(50),
});
export const updateRecipeRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  notes: z.string().trim().min(1).max(300).nullable().optional(),
  active: z.boolean().optional(),
  components: z.array(componentInput).min(1).max(50).optional(),
});

export const consumptionRunResultSchema = z.object({
  processed: z.number().int(),
  posted: z.number().int(),
  shortfall: z.number().int(),
  no_recipe: z.number().int(),
  failed: z.number().int(),
  reversed: z.number().int(),
  kept_as_waste: z.number().int(),
  remaining: z.number().int(),
});
export type ConsumptionRunResult = z.infer<typeof consumptionRunResultSchema>;

export const consumptionReportQuerySchema = z.object({ from: z.iso.date(), to: z.iso.date() });
export const consumptionReportSchema = z.object({
  currency: z.string(),
  from: z.iso.date(),
  to: z.iso.date(),
  definitions: z.record(z.string(), z.string()),
  /** Accepted items not yet processed (the background job runs every minute). */
  backlog: z.number().int(),
  ingredients: z.array(
    z.object({
      item_id: uuidSchema,
      sku: z.string(),
      item_name: z.string(),
      base_unit: z.enum(['g', 'ml', 'pcs']),
      required_scaled: z.number().int(),
      posted_scaled: z.number().int(),
      shortfall_scaled: z.number().int(),
      value_minor: z.number().int(),
    }),
  ),
  shortfalls: z.array(
    z.object({
      order_item_id: uuidSchema,
      item_name: z.string(),
      consumed_at: isoTimestampSchema,
      status: z.enum(['shortfall', 'failed']),
      error: z.string().nullable(),
    }),
  ),
  totals: z.object({
    items: z.number().int(),
    no_recipe: z.number().int(),
    value_minor: z.number().int(),
  }),
});
export type ConsumptionReport = z.infer<typeof consumptionReportSchema>;
